import { AfterViewInit, Component, ComponentFactoryResolver, TemplateRef, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { CustomBehaviourHostDirective, PaginatedItems, snapshot } from '../../../../global';
import { AlertService } from '../../../../global/alert/alert';
import { AdminEntityService } from '../../service/entity.service';
import { EntityFormConfig } from '../../service/entity-form-config';

import { BaseEntityRowComponent } from './base-entity-row.component';

@Component({
    standalone: false,
    selector: 'wla-entity-list',
    template: `
        <abs-page-layout
            *ngIf="entityTypeMetaData$ | async as entityTypeMetaData"
            [pageTitle]="entityTypeMetaData.title"
            [subTitle]="gameId$ | async"
        >
            <abs-entity-list-filters [gameId]="gameId$ | async" [entityType]="entityType$ | async"></abs-entity-list-filters>
            <ng-container *ngIf="entities$ | async as entities">
                <div class="content">
                    <ion-button (click)="newEntity()"> New {{ entityTypeMetaData.entityName }} </ion-button>
                    <ion-button (click)="saveAll()"> Save All </ion-button>
                    @if (stagingOnly$ | async) {
                        <ion-button [color]="'warning'" (click)="promoteStagingOnly(entities.itemCount)">
                            Promote {{ entities.itemCount }} {{ entityTypeMetaData.entityName }}(s) to production
                        </ion-button>
                    }
                    @if (stat$ | async; as stat) {
                        <ion-button
                            [color]="'warning'"
                            *ngIf="getStatActionLabel(stat, entities.itemCount) as label"
                            (click)="executeAction(stat, entities.itemCount)"
                        >
                            {{ label }}
                        </ion-button>
                    }
                </div>
                <abs-pager [items]="entities"></abs-pager>
                <ng-template #defaultRowTemplate let-item="item">
                    <ion-label>
                        <p>
                            <strong>{{ item.name[lang] }}</strong>
                        </p>
                        <p *ngIf="item.description?.[lang]">{{ item.description?.[lang] }}</p>
                    </ion-label>
                </ng-template>
                <ng-template customBehaviourHost></ng-template>
                <ion-list *ngIf="rowTemplate$ | async as rowTemplate">
                    <ion-item *ngFor="let e of entities.items" [routerLink]="currentUrl + '/' + e._id">
                        <ng-template [ngTemplateOutlet]="rowTemplate" [ngTemplateOutletContext]="{ item: e }"></ng-template>
                    </ion-item>
                </ion-list>
                <abs-pager [items]="entities"></abs-pager>
            </ng-container>
        </abs-page-layout>
    `,
    styles: [
        `
            abs-entity-list-filters {
                display: block;
                margin: 0 0 10px;
            }
        `
    ]
})
export class EntityListComponent implements AfterViewInit, OnDestroy {
    currentUrl = this.router.url.split('?')[0]; // Hacky fix to bug where links don't work after paging
    lang = 'en';
    filterValue$ = new BehaviorSubject('');
    gameId$ = this.route.params.pipe(map((p) => p.gameId));
    entityType$ = this.route.params.pipe(map((p) => p.entityType));
    entityTypeMetaData$ = this.route.params.pipe(map((p) => this.entityFormConfig.games[p.gameId].entityTypeMetaData[p.entityType]));
    entityRowTemplate$ = this.route.params.pipe(map((p) => this.entityFormConfig.games[p.gameId]?.entityRowTemplates?.[p.entityType]));
    formConfig$ = combineLatest([this.gameId$, this.entityType$]).pipe(
        switchMap(([gameId, entityType]) => this.entityFormConfig.games[gameId].forms[entityType])
    );
    stagingOnly$ = this.route.queryParams.pipe(map((p) => !!p.stagingOnly));
    stat$ = this.route.queryParams.pipe(map((p) => p.stat as string | undefined));
    entities$ = combineLatest([this.gameId$, this.entityType$, this.route.queryParams]).pipe(
        filter(([gameId, entityType]) => !!gameId && !!entityType),
        switchMap(([gameId, entityType, params]) => {
            if (params.stat) {
                return this.entityService.getEntitiesByStat(
                    params.stat,
                    gameId,
                    entityType,
                    params.skip || 0,
                    params.sort,
                    10,
                    !!params.diff
                );
            }
            if (params.stagingOnly) {
                return this.entityService.getStagingOnlyEntities(gameId, entityType, params.skip || 0, params.sort);
            }
            return this.entityService.getEntities(gameId, entityType, params.skip || 0, params.query, params.sort);
        })
    );

    private statActionMeta: Record<string, { label: string }> = {
        beta: { label: 'Remove Beta role from' },
        earlyAccess: { label: 'Promote to General Access' },
        missingAnnual: { label: 'Fix Missing Annual Subscriber role for' },
        entity: { label: 'Promote to Production' },
        stagingOnly: { label: 'Promote to Production' }
    };

    getStatActionLabel(stat: string, count: number): string | null {
        const meta = this.statActionMeta[stat];
        if (!meta) return null;
        return `${meta.label} ${count} entities`;
    }

    @ViewChild(CustomBehaviourHostDirective) customBehaviours: CustomBehaviourHostDirective;
    @ViewChild('defaultRowTemplate') defaultRowTemplate: TemplateRef<any>;

    rowTemplate$: BehaviorSubject<TemplateRef<any>> = new BehaviorSubject(null);
    rowTemplateLoaded = false;
    rowSub: Subscription;

    constructor(
        private entityService: AdminEntityService,
        public route: ActivatedRoute,
        public router: Router,
        private entityFormConfig: EntityFormConfig,
        private componentFactoryResolver: ComponentFactoryResolver,
        private alertService: AlertService
    ) {}

    ngAfterViewInit() {
        this.rowTemplate$.next(this.defaultRowTemplate);
        this.entities$.subscribe(() => {
            this.loadCustomRow();
        });
    }

    newEntity() {
        snapshot(
            combineLatest([this.gameId$, this.entityType$, this.entityTypeMetaData$, this.formConfig$]),
            ([gameId, entityType, entityTypeMetaData, formConfig]) => {
                // TODO: A more flexible way to provide a default value
                const entity = {
                    ...(formConfig.defaultValue ?? {}),
                    name: {
                        en: `(New ${entityTypeMetaData.entityName})`
                    },
                    description: {
                        en: ''
                    },
                    gameId
                };
                this.entityService
                    .saveEntity(entity, entity.gameId, entityType)
                    .toPromise()
                    .then((res) => {
                        console.log(`Created new entity: ${res[0]}`);
                        this.router.navigateByUrl(`${this.currentUrl}/${res[0]}`);
                    });
            }
        );
    }

    async promoteStagingOnly(count: number) {
        const { role } = await this.alertService.showConfirm('Promote to Production', `Promote ${count} entities to production?`);
        if (role !== 'ok') return;
        combineLatest([this.gameId$, this.entityType$])
            .pipe(take(1))
            .subscribe(([gameId, entityType]) => {
                this.entityService.promoteStagingOnlyEntities(gameId, entityType).subscribe(() => {
                    this.router.navigate([this.currentUrl]);
                });
            });
    }

    async executeAction(stat: string, count: number) {
        const label = this.getStatActionLabel(stat, count);
        const { role } = await this.alertService.showConfirm(label, `Are you sure you want to proceed?`);
        if (role !== 'ok') return;
        combineLatest([this.gameId$, this.entityType$])
            .pipe(take(1))
            .subscribe(([gameId, entityType]) => {
                this.entityService.executeStatAction(stat, gameId, entityType).subscribe(() => {
                    this.router.navigate([this.currentUrl]);
                });
            });
    }

    loadCustomRow() {
        if (this.rowTemplateLoaded) {
            return;
        }
        setTimeout(() => {
            this.rowTemplateLoaded = true;
            const gameId = this.route.snapshot.params.gameId;
            const entityType = this.route.snapshot.params.entityType;
            const component = this.entityFormConfig.games[gameId]?.entityRowTemplates?.[entityType];

            if (!component || !this.customBehaviours) {
                this.rowTemplate$.next(this.defaultRowTemplate);
                return;
            }

            const componentFactory = this.componentFactoryResolver.resolveComponentFactory<BaseEntityRowComponent>(component);

            const viewContainerRef = this.customBehaviours.viewContainerRef;
            viewContainerRef.clear();
            const componentRef = viewContainerRef.createComponent(componentFactory);

            this.rowSub = componentRef.instance.rowTemplate$.subscribe((t) => {
                this.rowTemplate$.next(t);
            });
        }, 100);
    }

    saveAll() {
        if (confirm('Are you sure?')) {
            snapshot(combineLatest([this.gameId$, this.entityType$]), ([gameId, entityType]) => {
                this.entityService
                    .getEntities(gameId, entityType, 0, '', '', 2000)
                    .pipe(
                        switchMap((entities: PaginatedItems) => {
                            if (entities.itemCount !== entities.items.length) {
                                const msg = `Entity count mismatch: itemCount is ${entities.itemCount} but ${entities.items.length} items received`;
                                console.error(msg);
                                throw new Error(msg);
                            }
                            return this.entityService.saveMany(entities.items, gameId, entityType);
                        })
                    )
                    .subscribe((res) => {
                        console.log({ res });
                    });
            });
        }
    }

    ngOnDestroy() {
        if (this.rowSub) {
            this.rowSub.unsubscribe();
        }
    }
}
